import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DrunkButton } from '../components/DrunkButton';
import { DrunkCard } from '../components/DrunkCard';
import { useSession } from '../context/SessionContext';
import { ArrowLeft, RefreshCw, PartyPopper } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Rejoin = () => {
  const navigate = useNavigate();
  const { saveSession } = useSession();
  
  const [email, setEmail] = useState('');
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleFindSessions = async () => {
    if (!isValidEmail(email)) {
      toast.error("Enter a valid email!");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/sessions/rejoin`, {
        email: email.trim().toLowerCase()
      });
      
      setSessions(response.data.sessions || []);
      setSearched(true);
      
      if (response.data.sessions?.length > 0) {
        toast.success(`Found ${response.data.sessions.length} session(s)! 🎉`);
      }
    } catch (error) {
      const message = error.response?.data?.detail || "No sessions found";
      toast.error(message);
      setSessions([]);
      setSearched(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejoinSession = (session) => {
    saveSession({
      session_code: session.session_code,
      pin: session.pin,
      member_name: session.your_name,
      member_email: email.trim().toLowerCase()
    });
    
    toast.success("Welcome back! 🍺");
    navigate(`/session/${session.session_code}`);
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  };

  return (
    <div className="min-h-screen bg-cream p-4">
      <div className="max-w-md mx-auto">
        {/* Back Button */}
        <DrunkButton
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5 inline mr-1" />
          Home
        </DrunkButton>

        <div className="bounce-in">
          <DrunkCard variant="default" className="text-center p-5">
            <RefreshCw className="w-12 h-12 mx-auto mb-3 text-cyan" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold mb-2">
              Lost Your Party? 🔍
            </h2>
            <p className="font-body text-sm mb-6 text-black/70">
              Enter your email to find your sessions
            </p>
            
            <input
              type="email"
              placeholder="drunk@party.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-4 text-lg border-2 border-black rounded-xl mb-4 font-body shadow-brutal focus:outline-none focus:border-pink"
              maxLength={100}
              data-testid="rejoin-email-input"
              autoComplete="email"
              onKeyPress={(e) => e.key === 'Enter' && handleFindSessions()}
            />
            
            <DrunkButton
              variant="primary"
              size="large"
              onClick={handleFindSessions}
              disabled={!email.trim() || !isValidEmail(email) || isLoading}
              className="w-full py-4"
              data-testid="find-sessions-btn"
            >
              {isLoading ? 'Searching...' : 'Find My Sessions 🔍'}
            </DrunkButton>
          </DrunkCard>
        </div>

        {/* Sessions List */}
        {searched && (
          <div className="mt-6 space-y-3">
            {sessions.length > 0 ? (
              <>
                <h3 className="font-heading text-lg font-bold text-center mb-4">
                  Your Active Parties 🎉
                </h3>
                {sessions.map((session, index) => (
                  <DrunkCard 
                    key={index}
                    variant="cyan"
                    className="p-4 cursor-pointer hover:scale-[1.02] transition-transform"
                    onClick={() => handleRejoinSession(session)}
                    data-testid={`session-card-${index}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-mono font-bold text-lg">{session.session_code}</p>
                        <p className="font-body text-sm">You: {session.your_name}</p>
                        <p className="font-body text-xs opacity-70">{formatDate(session.created_at)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">{session.member_count} 🍺</p>
                        <p className="text-xs">members</p>
                      </div>
                    </div>
                    <div className="mt-2 text-center">
                      <span className="text-xs font-bold bg-white px-2 py-1 rounded-full border border-black">
                        Tap to rejoin →
                      </span>
                    </div>
                  </DrunkCard>
                ))}
              </>
            ) : (
              <DrunkCard variant="yellow" className="text-center p-5">
                <p className="font-heading text-lg font-bold mb-2">No Active Sessions 😢</p>
                <p className="font-body text-sm mb-4">
                  Either they expired or this email wasn't used
                </p>
                <DrunkButton
                  variant="primary"
                  onClick={() => navigate('/session/new')}
                  className="w-full"
                >
                  <PartyPopper className="w-4 h-4 inline mr-2" />
                  Start a New Party!
                </DrunkButton>
              </DrunkCard>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Rejoin;
